import React from 'react';
import { Building, Clock, Users, Plug, AlertTriangle, X, LayoutGrid, Star } from 'lucide-react';
import { useBusinessSettings, BusinessSettingsSection } from '@/context/BusinessSettingsContext';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
  useSidebar
} from '@/components/ui/sidebar';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';

type NavItem = {
  id: BusinessSettingsSection;
  label: string;
  icon: React.ElementType;
  tooltip?: string;
};

const businessSettingsNavigation: NavItem[] = [
  {
    id: 'business',
    label: 'Business',
    icon: Building,
    tooltip: 'Name, contact, theme, and policies'
  },
  {
    id: 'hours',
    label: 'Hours',
    icon: Clock,
    tooltip: 'Opening hours and closed dates'
  },
  {
    id: 'staff',
    label: 'Staff',
    icon: Users,
    tooltip: 'Time tracking, shifts, and kiosk'
  },
  {
    id: 'reviews',
    label: 'Reviews',
    icon: Star,
    tooltip: 'Review assignment and routing rules'
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: Plug,
    tooltip: 'External system connections'
  },
  {
    id: 'features',
    label: 'Features',
    icon: LayoutGrid,
    tooltip: 'Enable or disable features for this business'
  },
  {
    id: 'danger',
    label: 'Danger zone',
    icon: AlertTriangle,
    tooltip: 'Delete this business'
  }
];

export const BusinessSettingsSidebarNav = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const { activeSection, setActiveSection, closeBusinessSettings } = useBusinessSettings();
  const { isMobile, setOpenMobile } = useSidebar();

  const handleItemClick = (item: NavItem) => {
    setActiveSection(item.id);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleClose = () => {
    closeBusinessSettings();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <div
      ref={ref}
      data-sidebar="navigation"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    >
      <SidebarGroup>
        <SidebarGroupLabel className="flex items-center justify-between pr-2">
          <span>Business Settings</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleClose}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Close settings</span>
          </Button>
        </SidebarGroupLabel>

        <SidebarMenu>
          {businessSettingsNavigation.map((item) => (
            <SidebarMenuItem key={item.id}>
              <SidebarMenuButton
                isActive={activeSection === item.id}
                tooltip={item.tooltip || item.label}
                onClick={() => handleItemClick(item)}
              >
                <item.icon />
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </div>
  );
});

BusinessSettingsSidebarNav.displayName = 'BusinessSettingsSidebarNav';
